/**
 * WebSocket Messages Interface Definitions
 * Shared between Frontend and Backend (Phase 1: Frontend -> Backend)
 */

import { RecordAccessLevel } from './data-types';

export { RecordAccessLevel } from './data-types';
export type { IConnectionAndUsername } from './data-types';

//============================================================================================================================
// Client -> Server commands
//============================================================================================================================

export type WsCommandType = 'create' | 'read' | 'update' | 'delete';

export type WsCommandSubject = 'records' | 'subrecords' | 'saasTenants' | 'analytics';

export interface IWsCommandMessage {
  action: string; // e.g. 'CommandsHandlerRouteKey'
  data: {
    command: IWsCommand;
  };
}

export interface IWsCommand {
  type: WsCommandType;
  params: IWsCommandParams;
}

/**
 * The params object can contain keys for any subject.
 * The specific content depends on the command type (create/read/update/delete),
 * so these are union types or loose interfaces to accommodate all operations.
 */
export interface IWsCommandParams {
  records?: IWsRecordsParams;
  subrecords?: IWsSubrecordsParams;
  saasTenants?: IWsSaasTenantsParams;
  analytics?: IWsAnalyticsParams;
  llmProfiles?: IWsLLMProfilesParams | boolean; // boolean used in read
  llmPrompts?: IWsLLMPromptsParams;
  usage?: IWsUsageParams;
}

export type WsUserText = string;
export type WsAIText = string;
export type WsRecordId = string;
export type WsSubrecordId = string;
export type WsLLMProfileId = string;

export enum SearchType {
  Text = 'Text',
  Semantic = 'Semantic',
}

//============================================================================================================================
// Client Metadata
//============================================================================================================================

export interface IClientMetadata {
  coords?: {
    latitude: number;
    longitude: number;
  };
  language?: string;
}

//============================================================================================================================
// Records
//============================================================================================================================

export interface IWsCreateRecordParams {
  record_id: WsRecordId;
  user_text: WsUserText;
  llm_profile_id: WsLLMProfileId;

  // Optional
  ai_text?: WsAIText;
  uploadedFiles?: string[];
  saas_tenant_id?: string; // For linking existing records

  access_level?: RecordAccessLevel;
  shareWithSaasTenantIds?: string[]; // Array of SaaS tenant IDs for restricted access

  clientMetadata?: IClientMetadata; // Client metadata (geolocation and language)
}

export interface IWsReadRecordsParams {
  latestPublic?: boolean;
  record_id?: WsRecordId;
  searchText?: string;
  searchType?: SearchType;
  readAllLinkable?: boolean;
  includeArchived?: boolean;
  limit?: number;
}

export interface IWsUpdateRecordParams {
  record_id: WsRecordId;

  // Fields to update
  user_text?: WsUserText;
  ai_text?: WsAIText;
  is_archived?: boolean;
  subject?: string;
  saas_tenant_id?: string;
  saas_tenant_name?: string;
  llm_profile_id?: WsLLMProfileId;

  access_level?: RecordAccessLevel;
  shareWithSaasTenantIds?: string[];
}

export interface IWsDeleteRecordParams {
  record_id: WsRecordId;
  saas_tenant_id?: string;
}

export type IWsRecordsParams = IWsCreateRecordParams | IWsReadRecordsParams | IWsUpdateRecordParams | IWsDeleteRecordParams;

//============================================================================================================================
// Subrecords
//============================================================================================================================

export interface IWsCreateSubrecordParams {
  subrecord_id: WsSubrecordId;
  record_id: WsRecordId;
  user_text: WsUserText;

  // Optional
  non_ai?: boolean;
  uploadedFiles?: string[];
  consolidateEntireChat?: string; // If present, contains the user text trigger

  // Client metadata (geolocation and language)
  clientMetadata?: IClientMetadata;
}

export interface IWsReadSubrecordsParams {
  record_id?: WsRecordId;
  fromFirstRecord?: boolean;
}

export interface IWsDeleteSubrecordParams {
  record_id: WsRecordId;
  subrecord_id: WsSubrecordId;
  replaceChatWithConsolidation?: boolean;
}

// Update not explicitly handled for subrecords in main switch, but good to have if needed
export interface IWsUpdateSubrecordParams {
  record_id: WsRecordId;
  subrecord_id: WsSubrecordId;
  [key: string]: any;
}

export type IWsSubrecordsParams = IWsCreateSubrecordParams | IWsReadSubrecordsParams | IWsDeleteSubrecordParams | IWsUpdateSubrecordParams;

//============================================================================================================================
// SaaS Tenants
//============================================================================================================================

export interface IWsCreateSaasTenantParams {
  saas_tenant_id: string;
  email: string;
  name: string;

  // Optional
  disabled?: boolean;
  phone?: string;
  address?: string;
  israeli_id?: string;
}

export interface IWsReadSaasTenantsParams {
  usage?: {
    start_date?: string;
    end_date?: string;
  };
  nameSubstring?: string; // For search functionality
}

export interface IWsUpdateSaasTenantParams {
  saas_tenant_id: string;
  name?: string;
  disabled?: boolean;
  email?: string;
  phone?: string;
  address?: string;
  israeli_id?: string;
  // other fields
  updated_at?: string;
}

export interface IWsDeleteSaasTenantParams {
  saas_tenant_id: string;
}

export type IWsSaasTenantsParams = IWsCreateSaasTenantParams | IWsReadSaasTenantsParams | IWsUpdateSaasTenantParams | IWsDeleteSaasTenantParams;

//============================================================================================================================
// Analytics / Usage
//============================================================================================================================

export interface IWsAnalyticsParams {
  subType: string; // e.g. 'usage-metrics'
  start_date?: string;
  end_date?: string;
}

export interface IWsUsageParams {
  // Usage usually read via saasTenants or dedicated usage param
  [key: string]: any;
}

//============================================================================================================================
// LLM Profiles & Prompts
//============================================================================================================================

export interface IWsCreateLLMProfileParams {
  profile_id: string;
  description?: string;
  model_id?: string;
}

export type IWsLLMProfilesParams = IWsCreateLLMProfileParams; // | boolean (handled in union above)

export interface IWsCreateLLMPromptParams {
  prompt_id: string;
  name: string;
  user_prompt: string;
  system_prompt?: string;
}

//============================================================================================================================
// Server -> Client payloads
//============================================================================================================================

export interface IWsDataReadPayload {
  llmProfiles?: any;
  records?: any;
  latestPublicRecords?: any;
  subrecords?: any;
  saasTenants?: any;

  // SaaS tenants search
  saasTenantSearchResults?: IWsSaasTenantSearchResult[];
  saasTenantSearchTooManyResults?: boolean;

  usage?: any;
  analytics?: any;
}

export interface IWsSaasTenantSearchResult {
  saas_tenant_id: string;
  name: string;
}

export type IWsLLMPromptsParams = IWsCreateLLMPromptParams;
